import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { defineMessages, useIntl } from 'react-intl';
import type {
  VisualDxBuiltDifferentialPayload,
  VisualDxFindingsPayload,
  VisualDxHandoutPayload,
  VisualDxNamedRow,
} from '../../contracts';
import { VisualDxDiagnosisRow } from './VisualDxDiagnosisRow';

import type { JSX } from 'react';

const messages = defineMessages({
  findingsHeading: {
    id: 'NMt3hf419O',
    defaultMessage: 'VisualDx findings',
    description: 'Heading above the VisualDx findings vocabulary',
  },
  findingsEmpty: {
    id: 'tOVj18Hrbu',
    defaultMessage: 'No matching VisualDx findings.',
    description: 'Empty state when VisualDx returns no findings',
  },
  differentialHeading: {
    id: 'b5igVFsRry',
    defaultMessage: 'VisualDx differential',
    description: 'Heading above a VisualDx-built differential',
  },
  differentialEmpty: {
    id: 'MP6eHb4Lt0',
    defaultMessage: 'No diagnoses matched those findings.',
    description: 'Empty state when VisualDx returns no differential',
  },
  handoutHeading: {
    id: 'SVtuQ2pJXf',
    defaultMessage: 'Patient handout',
    description:
      'Heading above a VisualDx patient handout when the diagnosis name is unknown',
  },
  handoutHeadingForDiagnosis: {
    id: '7F7NfaV/YD',
    defaultMessage: 'Patient handout — {diagnosis}',
    description: 'Heading above a VisualDx patient handout for a named diagnosis',
  },
  handoutEmpty: {
    id: 'dhbT2HuU4Q',
    defaultMessage: 'No VisualDx patient handout is available for this diagnosis.',
    description: 'Empty state when VisualDx has no handout',
  },
  disclaimer: {
    id: 'JpQZRw3A3o',
    defaultMessage: 'VisualDx candidates, not a diagnosis. Confirm clinically.',
    description: 'Footer under VisualDx findings and differential cards',
  },
});

const containerClass =
  'flex w-full flex-col gap-2.5 rounded-xl bg-background-secondary p-4 text-text-primary';

const headerClass = 'flex items-baseline justify-between gap-2 pb-1';

const Disclaimer = (): JSX.Element => {
  const intl = useIntl();
  return (
    <TypographyCaption className="text-text-secondary">
      {intl.formatMessage(messages.disclaimer)}
    </TypographyCaption>
  );
};

const RowList = ({ rows }: { rows: VisualDxNamedRow[] }): JSX.Element => (
  <>
    {rows.map((row, idx) => (
      <VisualDxDiagnosisRow
        key={`${idx}-${row.name}`}
        name={row.name}
        imageUrl={row.image_url ?? null}
        description={row.summary}
        href={row.visualdx_url}
        ariaLabel={row.name}
      />
    ))}
    <Disclaimer />
  </>
);

export const VisualDxFindings = ({
  findings,
}: {
  findings: VisualDxFindingsPayload;
}): JSX.Element => {
  const intl = useIntl();
  const items = findings.items ?? [];
  if (items.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.findingsEmpty)}
        </TypographyCaption>
        <Disclaimer />
      </div>
    );
  }
  return (
    <div className={containerClass}>
      <header className={headerClass}>
        <TypographyP2Bold>
          {intl.formatMessage(messages.findingsHeading)}
        </TypographyP2Bold>
      </header>
      <RowList rows={items} />
    </div>
  );
};

export const VisualDxBuiltDifferential = ({
  builtDifferential,
}: {
  builtDifferential: VisualDxBuiltDifferentialPayload;
}): JSX.Element => {
  const intl = useIntl();
  const diagnoses = builtDifferential.diagnoses ?? [];
  if (diagnoses.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.differentialEmpty)}
        </TypographyCaption>
        <Disclaimer />
      </div>
    );
  }
  return (
    <div className={containerClass}>
      <header className={headerClass}>
        <TypographyP2Bold>
          {intl.formatMessage(messages.differentialHeading)}
        </TypographyP2Bold>
      </header>
      <RowList rows={diagnoses} />
    </div>
  );
};

export const VisualDxHandout = ({
  handout,
}: {
  handout: VisualDxHandoutPayload;
}): JSX.Element => {
  const intl = useIntl();
  const sections = handout.sections ?? [];
  if (!handout.available || sections.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.handoutEmpty)}
        </TypographyCaption>
        <Disclaimer />
      </div>
    );
  }
  return (
    <div className={containerClass}>
      <header className={headerClass}>
        <TypographyP2Bold>
          {handout.diagnosis
            ? intl.formatMessage(messages.handoutHeadingForDiagnosis, {
                diagnosis: handout.diagnosis,
              })
            : intl.formatMessage(messages.handoutHeading)}
        </TypographyP2Bold>
      </header>
      {(handout.images ?? []).length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {(handout.images ?? []).slice(0, 4).map((src) => (
            // VisualDx thumbnails are inlined by our API after an authenticated
            // fetch. next/image domain rules do not apply to data URIs.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={src.slice(0, 48)}
              src={src}
              alt=""
              className="size-24 rounded-lg object-cover"
            />
          ))}
        </div>
      ) : null}
      {sections.map((section, idx) => (
        <div key={`${idx}-${section.title}`} className="flex flex-col gap-1">
          {section.title ? <TypographyP2Bold>{section.title}</TypographyP2Bold> : null}
          {section.body ? (
            <TypographyCaption className="text-text-secondary">
              {section.body}
            </TypographyCaption>
          ) : null}
        </div>
      ))}
      <Disclaimer />
    </div>
  );
};
